/**
 * Bid Submission Component
 * 
 * A comprehensive component for vendors to submit competitive bids on RFQs (Requests for Quotation).
 * This component handles the complete bid lifecycle including submission, viewing, and withdrawal.
 * 
 * **Key Features:**
 * - Multi-specification bid pricing with quantity option matrix
 * - Automatic specification and quantity option loading from RFQ
 * - Line-item pricing validation (all items must be priced)
 * - Bid validity period management
 * - Required attestations before submission
 * - Bid withdrawal with reason tracking
 * - Display of existing bid details
 * - Decline to bid functionality
 * 
 * **Bid Lifecycle:**
 * 1. **Viewing RFQ**: Vendor sees RFQ details and specifications
 * 2. **Pricing**: Vendor enters unit prices for each specification and quantity option
 * 3. **Attestation**: Vendor confirms binding agreement and terms acceptance
 * 4. **Submission**: Bid is locked and submitted (cannot be edited)
 * 5. **Optional Withdrawal**: Vendor can withdraw before RFQ closes
 * 
 * **Validation Requirements:**
 * - All line items must have prices > 0
 * - All line items must have lead times > 0
 * - Bid validity date must be after RFQ close date
 * - Both attestation checkboxes must be checked
 * - Number of line items must match expected count (specs × quantity options)
 * 
 * **Bid States:**
 * - Can Submit: RFQ is open, before close date, no existing bid
 * - Can Withdraw: Bid exists, status is 'submitted', before close date
 * - View Only: Bid exists but cannot be modified or withdrawn
 * 
 * @module BidSubmission
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useBids } from '@/hooks/useBids';
import { DeclineToBidModal } from '@/components/rfq/DeclineToBidModal';
import { formatCurrency, parseCurrency, BidSubmissionData } from '@/lib/types/job';
import { CheckCircle, XCircle, AlertCircle, FileText, FileEdit, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { BidLineItemsTable } from './BidLineItemsTable';
import { BidLineItemsView } from './BidLineItemsView';
import { useBidLineItems, BidLineItemInput } from '@/hooks/useBidLineItems';
import { useRFQQuantityOptions } from '@/hooks/useRFQQuantityOptions';
import { useVendorNavCounts } from '@/contexts/VendorNavCountsContext';
import { supabase } from '@/integrations/supabase/client';
import { useVendorProfileEnhanced } from '@/hooks/useVendorProfileEnhanced';
import { useVendorLocations } from '@/hooks/useVendorLocations';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { SpecificationDocumentCard } from '@/components/specifications/SpecificationDocumentCard';
import { SpecificationDataRenderer } from '@/components/specifications/SpecificationDataRenderer';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';

/**
 * Props for the BidSubmission component
 */
interface BidSubmissionProps {
  /** ID of the RFQ to bid on */
  rfqId: string;
  /** Current status of the RFQ (open, closed, etc.) */
  rfqStatus: string;
  /** ISO timestamp when the RFQ bidding closes */
  closesAt: string;
}

/**
 * BidSubmission Component
 * 
 * Provides a complete interface for vendors to create, submit, view, and withdraw bids
 * on RFQs. Handles complex multi-specification pricing with quantity options.
 * 
 * The component automatically:
 * - Loads RFQ specifications and quantity options
 * - Validates all pricing inputs before allowing submission
 * - Sets default bid validity period (7 days after RFQ close)
 * - Displays existing bid if vendor has already submitted
 * - Shows appropriate actions based on bid status and timing
 * - Handles bid withdrawal with reason tracking
 * 
 * @param props - Component props
 * @param props.rfqId - ID of the RFQ
 * @param props.rfqStatus - Current RFQ status
 * @param props.closesAt - When bidding closes
 * 
 * @example
 * ```tsx
 * function RFQDetailsPage({ rfq }) {
 *   return (
 *     <div>
 *       <RFQDetails rfq={rfq} />
 *       <BidSubmission
 *         rfqId={rfq.id}
 *         rfqStatus={rfq.status}
 *         closesAt={rfq.closes_at}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export const BidSubmission = ({ rfqId, rfqStatus, closesAt }: BidSubmissionProps) => {
  const { bids, loading, submitBid, resubmitBid, withdrawBid } = useBids(rfqId, false);
  const { quantityOptions, loading: qtyLoading } = useRFQQuantityOptions(rfqId);
  const { profile: vendorProfile, loading: profileLoading } = useVendorProfileEnhanced();
  const { locations: vendorLocations, loading: vendorLocationsLoading } = useVendorLocations(vendorProfile?.user_id);
  const { isConnected: hasStripeConnect, isLoading: stripeConnectLoading } = useStripeConnect(vendorProfile?.organization_id || '');
  const { refreshCounts } = useVendorNavCounts();
  const { data: taxEnabledSetting, isLoading: taxSettingLoading } = usePlatformSetting('vendor_based_tax_enabled');
  const vendorTaxEnabled = taxEnabledSetting?.setting_value === 'true';
  const hasVendorLocations = vendorLocations.length > 0;
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [specifications, setSpecifications] = useState<any[]>([]);
  const [lineItems, setLineItems] = useState<BidLineItemInput[]>([]);
  const [existingLineItems, setExistingLineItems] = useState<any[]>([]);
  const [showWithdrawnBidDetails, setShowWithdrawnBidDetails] = useState(false);
  const [taxCalculations, setTaxCalculations] = useState<any[]>([]);
  const [totalVendorTax, setTotalVendorTax] = useState(0);
  
  const [bidForm, setBidForm] = useState({
    lead_time_days: 30,
    notes: '',
    attachments: [],
    valid_until: '',
    attestation_data: {
      requirements_reviewed: false
    }
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);

  // Check vendor compliance
  const checkVendorCompliance = (profile: any, hasLocations: boolean, hasStripeConnect: boolean) => {
    if (!profile) {
      return { 
        isCompliant: false, 
        issues: ['No vendor profile found. Please complete your vendor profile.'] 
      };
    }

    const issues = [];
    
    if (profile.is_verified === false) {
      issues.push('Profile not verified by admin. Please request verification from your profile page.');
    }
    
    if (!hasLocations) {
      issues.push('No fulfillment locations registered. Add at least one location in your Company Profile.');
    }
    
    if (!hasStripeConnect) {
      issues.push('Stripe Connect account not set up. Complete your payment setup in Account Settings to receive payments.');
    }
    
    return {
      isCompliant: issues.length === 0,
      issues
    };
  };

  const compliance = checkVendorCompliance(vendorProfile, hasVendorLocations, hasStripeConnect);
  
  // Categorize compliance issues for conditional UI
  const hasProfileIssues = compliance.issues.some(issue => 
    issue.includes('vendor profile') || 
    issue.includes('verified') || 
    issue.includes('locations')
  );
  const hasPaymentIssues = compliance.issues.some(issue => 
    issue.includes('Stripe Connect')
  );
  
  // Separate active bids from withdrawn bids
  const activeBid = bids.find(b => b.status !== 'withdrawn');
  const withdrawnBid = bids.find(b => b.status === 'withdrawn');
  const existingBid = activeBid || withdrawnBid; // For display purposes
  
  const isBeforeClose = new Date() < new Date(closesAt);
  const isRfqOpen = rfqStatus === 'open' && isBeforeClose;
  
  // Can submit new bid if no active bid (withdrawn bids allow resubmission)
  const canSubmitBid = isRfqOpen && !activeBid && compliance.isCompliant;
  const isResubmission = canSubmitBid && withdrawnBid;
  const canWithdraw = activeBid && activeBid.status === 'submitted' && isBeforeClose;

  // Fetch RFQ specifications
  useEffect(() => {
    const fetchSpecs = async () => {
      try {
        // First get the job_id
        const { data: rfqData, error: rfqError } = await supabase
          .from('rfqs')
          .select('job_id')
          .eq('id', rfqId)
          .single();
        
        if (rfqError) throw rfqError;
        if (!rfqData?.job_id) throw new Error('RFQ not found');
        
        // Fetch specifications directly from job_specifications (no join needed)
        const { data, error } = await supabase
          .from('job_specifications')
          .select('*')
          .eq('job_id', rfqData.job_id)
          .order('specification_order', { ascending: true });
        
        if (error) throw error;
        
        if (data) {
          setSpecifications(data.map((s: any) => ({
            id: s.id,
            specification_id: s.specification_id || s.id,
            name: s.specification_name,
            specification_document_id: s.specification_document_id,
            content: s.content,
            specification_name: s.specification_name,
            specification_type: s.specification_type,
            is_mandatory: s.is_mandatory,
            specifications: {
              name: s.specification_name,
              specification_document_id: s.specification_document_id,
              content: s.content
            }
          })));
          console.log('Fetched specifications:', data);
        }
      } catch (error) {
        console.error('Error fetching specifications:', error);
        toast.error('Failed to load specifications');
      }
    };
    
    if (rfqId) fetchSpecs();
  }, [rfqId]);

  // Fetch existing line items if bid exists (with tax data)
  useEffect(() => {
    const fetchExistingLineItems = async () => {
      if (!existingBid) return;
      
      // Fetch line items
      const { data: lineItemsData } = await supabase
        .from('bid_line_items')
        .select(`
          *,
          origin_location:vendor_locations!origin_location_id(
            id,
            location_name,
            city,
            state
          )
        `)
        .eq('bid_id', existingBid.id);
      
      // Fetch tax calculations for this bid
      const { data: taxCalcsData } = await supabase
        .from('bid_line_item_tax_calculations')
        .select('bid_line_item_id, vendor_tax_cents')
        .eq('bid_id', existingBid.id);
      
      if (lineItemsData) {
        // Aggregate tax amounts per line item (sum all destination taxes)
        const taxByLineItem = (taxCalcsData || []).reduce((acc, calc) => {
          const key = calc.bid_line_item_id;
          acc[key] = (acc[key] || 0) + (calc.vendor_tax_cents || 0);
          return acc;
        }, {} as Record<string, number>);
        
        // Merge tax data into line items
        const lineItemsWithTax = lineItemsData.map(item => ({
          ...item,
          tax_cents: taxByLineItem[item.id] || 0
        }));
        
        setExistingLineItems(lineItemsWithTax);
      }
    };
    
    if (existingBid) fetchExistingLineItems();
  }, [existingBid]);

  useEffect(() => {
    // Set default valid_until to 7 days after RFQ closes
    const defaultValidUntil = new Date(closesAt);
    defaultValidUntil.setDate(defaultValidUntil.getDate() + 7);
    setBidForm(prev => ({
      ...prev,
      valid_until: defaultValidUntil.toISOString().slice(0, 16)
    }));
  }, [closesAt]);

  const isFormValid = () => {
    const attestation = bidForm.attestation_data as any;
    const expectedLineItemCount = specifications.reduce((count, spec) => {
      const opts = quantityOptions.filter(qo => qo.specification_id === spec.specification_id);
      return count + opts.length;
    }, 0);
    
    // Tax must be calculated if feature is enabled
    const taxRequirementMet = !vendorTaxEnabled || taxCalculations.length > 0;
    
    return (
      lineItems.length === expectedLineItemCount &&
      lineItems.every(li => li.unit_price_cents > 0 && (li.lead_time_days || 0) > 0) &&
      bidForm.valid_until &&
      new Date(bidForm.valid_until) > new Date(closesAt) &&
      attestation?.requirements_reviewed &&
      taxRequirementMet
    );
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Please complete all required fields and price all quantity options');
      return;
    }

    setSubmitting(true);
    try {
      // Calculate max lead time from line items for the bid record
      const maxLeadTime = lineItems.reduce(
        (max, item) => Math.max(max, item.lead_time_days || 0),
        0
      );
      
      // Convert datetime-local to UTC ISO string to preserve timezone
      const processedBidForm = {
        ...bidForm,
        lead_time_days: maxLeadTime || bidForm.lead_time_days,
        valid_until: new Date(bidForm.valid_until).toISOString()
      };
      
      if (isResubmission && withdrawnBid) {
        // Resubmit by updating the withdrawn bid - pass tax calculations
        await resubmitBid(withdrawnBid.id, processedBidForm, lineItems, taxCalculations.length > 0 ? taxCalculations : undefined);
        toast.success('Bid resubmitted successfully!');
      } else {
        // Pass tax calculations to submitBid
        await submitBid(processedBidForm, lineItems, taxCalculations.length > 0 ? taxCalculations : undefined);
        toast.success('Bid submitted successfully!');
      }
      
      setLineItems([]);
      
      // Refresh nav counts immediately (RFQ count decreases, Bids count increases)
      refreshCounts();
    } catch (err) {
      toast.error('Failed to submit bid: ' + (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawReason.trim()) {
      toast.error('Please provide a reason for withdrawal');
      return;
    }

    setWithdrawing(true);
    try {
      await withdrawBid(existingBid!.id, withdrawReason);
      toast.success('Bid withdrawn successfully');
      setShowWithdrawForm(false);
      setWithdrawReason('');
    } catch (err) {
      toast.error('Failed to withdraw bid: ' + (err as Error).message);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading || qtyLoading || profileLoading || vendorLocationsLoading || stripeConnectLoading || taxSettingLoading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      {/* Existing Active Bid Display (not withdrawn) */}
      {activeBid && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Your Bid
              <Badge variant={
                activeBid.status === 'winner' ? 'default' :
                activeBid.status === 'loser' ? 'destructive' :
                'outline'
              }>
                {activeBid.status}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {existingLineItems.length > 0 ? (
              <BidLineItemsView 
                lineItems={existingLineItems}
                specifications={specifications.map(s => ({ id: s.id, name: s.name }))}
                hideGrandTotal={true}
                showFulfillmentLocation={true}
                totalVendorTaxCents={activeBid?.total_vendor_tax_cents || 0}
              />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Status</Label>
                  <p className="text-lg font-semibold">{activeBid.status}</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Submitted</Label>
                  <p className="text-lg font-semibold">{new Date(activeBid.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            )}
            
            {activeBid.notes && (
              <div>
                <Label className="text-sm font-medium">Notes</Label>
                <p className="text-sm text-muted-foreground">{activeBid.notes}</p>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              Submitted: {new Date(activeBid.created_at).toLocaleString()}
            </div>

            {canWithdraw && (
              <div className="pt-4 border-t">
                {!showWithdrawForm ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowWithdrawForm(true)}
                    className="text-red-600 hover:text-red-700"
                  >
                    Withdraw Bid
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Label htmlFor="withdraw-reason">Reason for withdrawal</Label>
                    <Textarea
                      id="withdraw-reason"
                      value={withdrawReason}
                      onChange={(e) => setWithdrawReason(e.target.value)}
                      placeholder="Please provide a reason for withdrawing your bid"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={handleWithdraw}
                        disabled={withdrawing || !withdrawReason.trim()}
                      >
                        {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowWithdrawForm(false);
                          setWithdrawReason('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vendor Compliance Check */}
      {!compliance.isCompliant && !existingBid && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>
            {hasProfileIssues && hasPaymentIssues 
              ? 'Profile & Payment Setup Incomplete'
              : hasPaymentIssues 
                ? 'Payment Setup Incomplete' 
                : 'Profile Incomplete'}
          </AlertTitle>
          <AlertDescription>
            <p className="mb-2">You must complete the following before submitting bids:</p>
            <ul className="list-disc list-inside space-y-1">
              {compliance.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
            <div className="flex gap-2 mt-3">
              {hasProfileIssues && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/profile/vendor'}
                >
                  Complete Profile
                </Button>
              )}
              {hasPaymentIssues && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/settings/account'}
                >
                  Setup Payment
                </Button>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Bid Submission / Resubmission Form */}
      {canSubmitBid && (
        <Card>
          <CardHeader>
            <CardTitle>{isResubmission ? 'Resubmit Your Bid' : 'Submit Your Bid'}</CardTitle>
            <CardDescription>
              {isResubmission 
                ? 'Submit a new bid to replace your previously withdrawn bid. This will update your existing bid record.'
                : 'Submit a competitive bid for this RFQ. Bids cannot be edited after submission.'
              }
            </CardDescription>
          </CardHeader>
          
          {/* Show withdrawn bid details if resubmitting */}
          {isResubmission && withdrawnBid && (
            <CardContent className="pt-0 pb-4">
              <Collapsible open={showWithdrawnBidDetails} onOpenChange={setShowWithdrawnBidDetails}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                    <span className="flex items-center gap-2">
                      <FileEdit className="h-4 w-4" />
                      View Previously Withdrawn Bid
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${showWithdrawnBidDetails ? 'rotate-180' : ''}`} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 p-4 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Badge variant="secondary">Withdrawn</Badge>
                    <span>on {new Date(withdrawnBid.updated_at).toLocaleDateString()}</span>
                  </div>
                  {withdrawnBid.notes && (
                    <div>
                      <Label className="text-xs text-muted-foreground">Previous Notes</Label>
                      <p className="text-sm whitespace-pre-wrap">{withdrawnBid.notes}</p>
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </CardContent>
          )}
          <CardContent className="space-y-6">

            <div className="border-t pt-6">
              <h4 className="font-medium text-lg mb-4">Pricing</h4>
              <BidLineItemsTable
                specifications={specifications}
                quantityOptions={quantityOptions}
                lineItems={lineItems}
                onChange={setLineItems}
                rfqId={rfqId}
                taxCalculations={taxCalculations}
                onTaxCalculationsChange={setTaxCalculations}
                totalVendorTax={totalVendorTax}
                onTotalVendorTaxChange={setTotalVendorTax}
              />
            </div>

            <div>
              <Label htmlFor="valid_until">Bid Valid Until *</Label>
              <Input
                id="valid_until"
                type="datetime-local"
                value={bidForm.valid_until}
                onChange={(e) => setBidForm(prev => ({ ...prev, valid_until: e.target.value }))}
                min={closesAt.slice(0, 16)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Bid must be valid beyond RFQ close date
              </p>
            </div>

            <div>
              <Label htmlFor="notes">Bid Comments (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add supplemental information regarding your bid"
                value={bidForm.notes}
                onChange={(e) => setBidForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                maxLength={2000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Add supplemental information regarding your bid • {bidForm.notes?.length || 0}/2000 characters
              </p>
            </div>

            {/* Attestations */}
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <h4 className="font-medium">Required Attestation</h4>
              
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="requirements_reviewed"
                  checked={(bidForm.attestation_data as any)?.requirements_reviewed || false}
                  onCheckedChange={(checked) => 
                    setBidForm(prev => ({
                      ...prev,
                      attestation_data: {
                        ...(prev.attestation_data as any),
                        requirements_reviewed: checked as boolean
                      }
                    }))
                  }
                />
                <Label htmlFor="requirements_reviewed" className="text-sm leading-relaxed">
                  I have reviewed all project specifications, delivery requirements, and due date.
                </Label>
              </div>
            </div>

            {/* Tax Calculation Required Warning */}
            {vendorTaxEnabled && taxCalculations.length === 0 && lineItems.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Tax calculation required.</strong> Please scroll up to the Pricing section and click "Calculate Tax" before submitting your bid.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid() || submitting}
                className="flex-1"
                size="lg"
              >
                {submitting 
                  ? (isResubmission ? 'Resubmitting Bid...' : 'Submitting Bid...') 
                  : (isResubmission ? 'Resubmit Bid' : 'Submit Bid')
                }
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeclineModalOpen(true)}
                disabled={submitting}
                className="flex-1"
                size="lg"
              >
                Decline to Bid
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Decline to Bid Modal */}
      <DeclineToBidModal
        open={declineModalOpen}
        onOpenChange={setDeclineModalOpen}
        rfqId={rfqId}
      />

      {/* Cannot Submit Messages */}
      {!canSubmitBid && !activeBid && !isResubmission && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground">
              {!compliance.isCompliant ? (
                <p>Please complete your vendor profile requirements above.</p>
              ) : (
                <>
                  {!isRfqOpen && <p>This RFQ is not open for bidding.</p>}
                  {!isBeforeClose && <p>The bidding window has closed.</p>}
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
